import React, { memo, useEffect, useState } from "react";
import { Alert, Button, Group, Loader, Menu, SegmentedControl } from "@mantine/core";
import { IconAlertCircle, IconEye, IconEyeOff } from "@tabler/icons-react";
import fetchApi from "../Lib/api";
import { useParams } from "react-router-dom";
import HeaderAndSidebar from "../Lib/HeaderAndSidebar";
import MainContainer from "../../stories/MainContainer";
import StartupHeader from "../../stories/StartupHeader";
import StartupBody from "../../stories/StartupBody";
import useStartupStore from "../../stores/startupStore";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import useLayoutStore from "../../stores/useLayoutStore";
import FullFeaturedKanbanWithMantine from "../../stories/FullFeaturedKanban";
import FullFeaturedGantt from "../../stories/Gantt";
import ProjectAlert from "../../stories/ProjectAlert";
import Mixpanel from "../Lib/mixpanel";
import moment from "moment";
import MobileRoadmapView from "./MobileRoadmapView";
import MobileProjectContainer from "./MobileProjectContainer";

const RoadmapHeader = styled.div`
  align-self: flex-start;
`;

const HeaderTitle = styled.h3`
  font-weight: var(--font-weight-h4);
  color: var(--color-background-card);
`;

const HeaderText = styled.p`
  color: #68706b;
  margin-top: 8px;
`;

const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  padding: 2rem;
  background-color: #f8f9fa;
`;

const NotFoundTitle = styled.h1`
  font-size: 3rem;
  font-weight: bold;
  color: #d9534f;
  margin-bottom: 1rem;
`;

const NotFoundDescription = styled.p`
  font-size: 1.25rem;
  color: #6c757d;
  margin-bottom: 1.5rem;
`;

const MemoStartupShow = memo(StartupShow);

// Custom hook for fetching startup data
const useStartup = (slug, setStartup, data) => {
  return useQuery({
    queryKey: ["startup", slug],
    queryFn: async () => {
      // Check if data is missing or null and throw error
      if (!data || data === null) {
        throw new Error('Startup data is missing or null');
      }
      
      setStartup(data);
      return data;
    },
    retry: 1,
    staleTime: 30 * 60 * 1000, 
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  refetchOnReconnect: false,
  });
};

export default function StartupContainer({ project, data }) {
  return (
    <HeaderAndSidebar>
      <MemoStartupShow project={project} data={data} />
    </HeaderAndSidebar>
  );
}

export const handleFollowUnfollow = async (
  isFollowing,
  slug,
  setIsFollowing
) => {
  try {
    const endpoint = !isFollowing
      ? `/startup_entities/${slug}/followings`
      : `/startup_entities/${slug}/followings/${slug}`;
    const method = !isFollowing ? "POST" : "DELETE";
    const response = await fetchApi(endpoint, method);

    if (response.ok) {
      const res = await response.json();
      if (setIsFollowing) setIsFollowing(!isFollowing);
    } else {
      const { error } = await response.json();
      console.error(error);
    }
  } catch (error) {
    console.error("Error during follow/unfollow:", error);
  }

  if (!setIsFollowing) return !isFollowing;
};

function StartupShow({ data }) {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [value, setValue] = useState("editor");
  const [isSyncing, setIsSyncing] = useState(false);

  const { setStartup, logTime, isOwner, isMember, openForApplication, isSubmitted, submittedAt } = useStartupStore(
    (state) => ({
      setStartup: state.setStartup,
      logTime: state.logTime,
      isOwner: state.isOwner,
      openForApplication: state.openForApplication,
      isMember: state.isMember,
      isSubmitted: state.isSubmitted,
      submittedAt: state.submittedAt
    })
  );

  const { setIsToggle } = useLayoutStore();

  const { isLoading, isError, error } = useStartup(slug, setStartup, data);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setIsToggle(!mobile);
    };
  
    checkMobile();
    window.addEventListener('resize', checkMobile);
  
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  

  // Log time spent on the page when the component unmounts or page unloads
  useEffect(() => {
    window.addEventListener("beforeunload", () => logTime(slug));

    return () => {
      window.removeEventListener("beforeunload", () => logTime(slug));
      logTime(slug); // Call logTime manually on unmount
    };
  }, [logTime, slug]);

  useEffect(() => {
    
    if (!isLoading ) {
      // Trigger sync after initial data is loaded
      syncEditorFromNotion();
    }
    // eslint-disable-next-line
  }, [isLoading ]);

  const syncEditorFromNotion = async () => {
    setIsSyncing(true);
    try {
      const res = await fetchApi(`/startup_entities/${slug}/sync_from_notion`,
        "POST"
      );
      const data = await res.json();
      if (res.ok) {
        // Update the roadmap in your store or local state
        setStartup(data);
      } 
    } catch (err) {
      console.log('error in syncing ', err)
    } finally {
      setIsSyncing(false);
    }
  };
  
  

  // Show loading indicator while the data is being fetched
  if (isLoading) {
    return (
      <Loader
        className="mx-auto mt-10"
        color="indigo"
        size="lg"
        variant="dots"
      />
    );
  }

  // Show error message if there's an error fetching the data
  if (isError) {
    console.log(error);
    return (
      <NotFoundContainer>
        <NotFoundTitle>
          404 - {t("startups_page.no_startup.not_found")}
        </NotFoundTitle>
        <NotFoundDescription>
          {t("startups_page.no_startup.desc")}
        </NotFoundDescription>
        <Button
          variant="outline"
          color="red"
          onClick={() => (window.location.href = "/dashboard/#/")}
        >
          {t("startups_page.no_startup.buttonText")}
        </Button>
      </NotFoundContainer>
    );
  }
  if (isMobile) {
    return <MobileProjectContainer />;
  }
  return (
    <MainContainer>
      <StartupHeader />
      {(data?.startup_entity?.project_type === "application" || data?.original_startup_entity?.project_type === "application") && (
        <ProjectAlert
          startupSlug={slug}
          owner={isOwner}
          member={isMember}
          openForApplication={openForApplication}
          currentUserId={data?.current_user_id}
        />
      )}
      <RoadmapHeader>
        <Group>
          <HeaderTitle>{t("startups_page.roadmaps")}</HeaderTitle>
         {(isOwner || isMember) &&  <SegmentedControl
            value={value}
            onChange={(newValue) => {
              setValue(newValue);
              Mixpanel.settings('Roadmap View', 'Changed', {
                oldValue: value, 
                newValue: newValue,
                context_page: 'Project Page',
                user_role: isOwner ? 'owner' : (isMember ? 'member' : 'guest')
              });
            }}
            size="md"
            data={[
              { label:t("startups_page.editor_title"), value: "editor" },
              { label: t("startups_page.gantt"), value: "gantt" },
              { label: t("startups_page.kanban"), value: "kanban" },
            ]}
          />}
          {isSubmitted && <p><b>Submitted at:</b> {moment(submittedAt).format('MMM D, YYYY h:mm A')}</p>}
        </Group>
      </RoadmapHeader>
      {value === "editor" && <StartupBody isSyncing={isSyncing}/>}
      {value === "gantt" && <FullFeaturedGantt />}
      {value === "kanban" && <FullFeaturedKanbanWithMantine />}

    </MainContainer>
  );
}

export const StartupPrivacy = ({ startup, setStartup }) => {
  const { t } = useTranslation();

  const handleSubmit = async (val) => {
    if (startup.startup_entity.public_listed === val) return;

    try {
      const response = await fetchApi(
        `/startup_entities/${startup.startup_entity.slug}`,
        "PATCH",
        {
          startup_entity: { public_listed: val },
        }
      );

      if (response.ok) {
        const res = (await response.json()).startup_entity;
        console.log(res);

        setStartup((p) => ({
          ...p,
          startup_entity: {
            ...p.startup_entity,
            public_listed: res.public_listed,
          },
        }));
      } else {
        const msg = (await response.json()).error;
        console.error(msg);
      }
    } catch (error) {
      console.error("Error during password change:", error);
    }
  };

  return (
    <Menu shadow="md">
      <Menu.Target>
        <Button
          variant="default"
          className="w-fit"
          size="md"
          leftIcon={
            startup.startup_entity.public_listed ? (
              <IconEye size="1.3rem" />
            ) : (
              <IconEyeOff size="1.3rem" />
            )
          }
        >
          {t("startups_page.privacy.privacy")}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <div className="relative flex items-start py-2 px-3 rounded-lg hover:bg-gray-100 ">
          <div className="flex items-center h-5 mt-1">
            <input
              id="public"
              type="radio"
              className="shrink-0 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none "
              onChange={() => handleSubmit(true)}
              checked={startup.startup_entity.public_listed === true}
            />
          </div>
          <label htmlFor="public" className="ms-3.5">
            <span className="block text-sm font-semibold text-gray-800 dark:text-gray-300">
              {t("startups_page.privacy.public")}
            </span>
            <span className="block text-sm text-gray-600 dark:text-gray-500">
              {t("startups_page.privacy.public_desc")}
            </span>
          </label>
        </div>
        <div className="relative flex items-start py-2 px-3 rounded-lg hover:bg-gray-100 ">
          <div className="flex items-center h-5 mt-1">
            <input
              id="private"
              type="radio"
              onChange={() => handleSubmit(false)}
              checked={startup.startup_entity.public_listed === false}
              className="shrink-0 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none "
            />
          </div>
          <label htmlFor="private" className="ms-3.5">
            <span className="block text-sm font-semibold text-gray-800 ">
              {t("startups_page.privacy.private")}
            </span>
            <span className="block text-sm text-gray-600 ">
              {t("startups_page.privacy.private_desc")}
            </span>
          </label>
        </div>
      </Menu.Dropdown>
    </Menu>
  );
};